import { useState } from 'react'

import { useAsyncCallback, useNotification } from '../../../../hooks'
import {
  getMassActionExcludeIdParam,
  getMassActionIdParam,
} from '../../../../utils/common'
import { getIdFilterParam } from '../../../../utils/filter'
import { getMassActionsSuccessItemsLength } from '../../../../utils/massActions'
import { getDocFlowAddRecipientFieldsEditInitialValues } from '../../DocFlowAddRecipientFields'
import {
  deleteDocFlowMappingResultItem,
  DOC_FLOW_MAPPING_RESULT_TABLE_ACTION_DELETE,
  DOC_FLOW_MAPPING_RESULT_TABLE_ACTION_EDIT_RECIPIENT,
  DOC_FLOW_MAPPING_RESULT_TABLE_ACTION_MAP_RECIPIENT,
  DOC_FLOW_MAPPING_RESULT_TABLE_MASS_ACTION_DELETE,
  DocFlowMappingProcessCell,
  DocFlowMappingRecipientModal,
  DocFlowMappingResultTableCell,
  getDocFlowMappingResultItemPosition,
  getDocFlowMapppingResultNotificationTitle,
  performDocFlowMappingResultMassAction,
  updateDocFlowMappingResultItem,
} from '../../DocFlowMapping'
import {
  DocFlowRecipientModal,
  getValidationSchemaDocFlowRecipientModalSingleField,
} from '../../DocFlowRecipientModal'
import { useDocFlowContext } from '../useDocFlowContext'
import { useDocFlowMappingContext } from '../useDocFlowMappingContext'
import { useDocFlowProhibitDeleteLastTableItem } from '../useDocFlowProhibitDeleteLastTableItem'

export function useDocFlowMappingResultTableActions({
  filterValues,
  ordering,
  pagination,
  selectionData,
  tableItems,
  onReloadTable,
}) {
  const { documentId, handleCloseModal, handleReloadDocFlow, setModal } =
    useDocFlowContext()

  const { activeFile, activeFileBytes, handleReloadFileProcess, processData } =
    useDocFlowMappingContext()

  const { handleAddErrorNotification, handleAddSuccessNotification } =
    useNotification()

  const [actionLoading, setActionLoading] = useState(false)

  const { excludeIds, handleCleanSelection, selectAllPages, selectedIds } =
    selectionData

  function handleSuccessAction({ action, data, totalCount }) {
    handleCloseModal()
    handleCleanSelection()

    const successItemsCount = getMassActionsSuccessItemsLength(data)

    const notificationHandler =
      successItemsCount === 0
        ? handleAddErrorNotification
        : handleAddSuccessNotification

    onReloadTable().finally(() => {
      notificationHandler({
        title: getDocFlowMapppingResultNotificationTitle({
          action,
          count: successItemsCount,
          totalCount,
        }),
      })
    })

    handleReloadDocFlow()
    handleReloadFileProcess()
  }

  function handleErrorAction(
    title = 'Notification.DocumentStaplingMappingResultMassActionErrorTitleKey'
  ) {
    handleCloseModal()
    handleAddErrorNotification(title)
  }

  const handleUpdateTableItem = useAsyncCallback(
    ({ action, data, item }) => {
      return updateDocFlowMappingResultItem({
        data,
        documentId,
        itemId: activeFile.itemId,
        resultItemId: item.id,
      })
        .then(({ data }) => {
          handleSuccessAction({ action, data })
        })
        .catch(() => {
          handleErrorAction()
        })
    },
    [activeFile, documentId, handleErrorAction, handleSuccessAction]
  )

  function getDeleteItemHandler({ action, item }) {
    return () => {
      setActionLoading(true)

      deleteDocFlowMappingResultItem({
        documentId,
        itemId: activeFile.itemId,
        resultItemId: item.id,
      })
        .then(({ data }) => {
          handleSuccessAction({ action, data })
        })
        .catch(() => {
          handleErrorAction()
        })
        .finally(() => {
          setActionLoading(false)
        })
    }
  }

  function massAction({ action, value = null }) {
    setActionLoading(true)

    const id = getMassActionIdParam({ selectAllPages, selectedIds })
    const excludeId = getMassActionExcludeIdParam({
      excludeIds,
      selectAllPages,
    })

    const params = {
      action,
      all: selectAllPages,
      filters: {
        ...filterValues,
        exclude_id: excludeId,
        id: getIdFilterParam({
          filterValues,
          ids: id,
          selectAllPages,
        }),
      },
      payload: value,
    }

    const countOfItems = selectAllPages ? pagination.count : id.length

    performDocFlowMappingResultMassAction({
      data: params,
      documentId,
      itemId: activeFile.itemId,
    })
      .then(({ data }) => {
        handleSuccessAction({ action, data, totalCount: countOfItems })
      })
      .catch(() => {
        handleErrorAction()
      })
      .finally(() => setActionLoading(false))
  }

  function getMassActionHandler({ action }) {
    return () => {
      massAction({ action })
    }
  }

  const { handleDelete } = useDocFlowProhibitDeleteLastTableItem({
    excludeIds,
    getDeleteItemHandler,
    getMassActionHandler,
    massActionName: DOC_FLOW_MAPPING_RESULT_TABLE_MASS_ACTION_DELETE,
    selectAllPages,
    selectedIds,
    tableItems,
    unfilteredTotal:
      processData?.[DocFlowMappingProcessCell.ResultSplittedDocuments],
  })

  function getHandleAddRecipientToItem({ action, item }) {
    return ({ value }) =>
      handleUpdateTableItem({
        action,
        data: {
          [DocFlowMappingResultTableCell.Recipient]:
            value[DocFlowMappingResultTableCell.Recipient].value,
        },
        item,
      })
  }

  function handleClickAddRecipient({ action, item }) {
    setModal(
      <DocFlowRecipientModal
        edit
        documentId={documentId}
        initialValues={getDocFlowAddRecipientFieldsEditInitialValues(item)}
        recipientFieldName={DocFlowMappingResultTableCell.Recipient}
        title='DocumentStapling.ModalEditRecipientTitleKey'
        validationSchema={getValidationSchemaDocFlowRecipientModalSingleField}
        onCloseModal={handleCloseModal}
        onSubmit={getHandleAddRecipientToItem({ action, item })}
      />
    )
  }

  function handleCloseMapRecipient({ hasChanges }) {
    handleCloseModal()

    if (hasChanges) {
      handleCleanSelection()
      onReloadTable()
      handleReloadDocFlow()
      handleReloadFileProcess()
    }
  }

  function handleClickMapRecipient(item) {
    setModal(
      <DocFlowMappingRecipientModal
        activeFile={activeFile}
        activeFileBytes={activeFileBytes}
        filterValues={filterValues}
        initialItem={item}
        initialPosition={getDocFlowMappingResultItemPosition({
          item,
          pagination,
          tableItems,
        })}
        initialTotal={pagination.count}
        ordering={ordering}
        onClose={handleCloseMapRecipient}
      />
    )
  }

  function setMassAction(action) {
    switch (action) {
      case DOC_FLOW_MAPPING_RESULT_TABLE_MASS_ACTION_DELETE:
        return handleDelete({ action })

      default:
        return null
    }
  }

  function setItemAction(action, item) {
    switch (action) {
      case DOC_FLOW_MAPPING_RESULT_TABLE_ACTION_DELETE:
        return handleDelete({ action, item })

      case DOC_FLOW_MAPPING_RESULT_TABLE_ACTION_EDIT_RECIPIENT:
        return handleClickAddRecipient({ action, item })

      case DOC_FLOW_MAPPING_RESULT_TABLE_ACTION_MAP_RECIPIENT:
        return handleClickMapRecipient(item)

      default:
        return null
    }
  }

  return {
    actionLoading,
    setItemAction,
    setMassAction,
  }
}
